import { LegalLayout, Section, Sub, P, UL, Strong, Code } from '../components/LegalLayout.jsx';

export default function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" effectiveDate="May 16, 2026" lastUpdated="May 16, 2026">
      <P>
        This Privacy Policy explains how <Strong>Pareto Labs Inc</Strong> (
        <Strong>Pareto</Strong>, <Strong>we</Strong>, <Strong>us</Strong>, or{' '}
        <Strong>our</Strong>) collects, uses, shares, and protects your personal information
        when you use our website at <Code>trypareto.ai</Code>, our WhatsApp and iMessage
        bots, and any related services (together, the <Strong>Service</Strong>).
      </P>
      <P>
        We've tried to write this in plain English. If anything is unclear, email us at{' '}
        <Code>legal@trypareto.ai</Code> and we'll explain.
      </P>

      <Section number={1} title="The short version">
        <UL>
          <li>
            We collect what we need to match you to jobs and paid research interviews, apply
            for jobs on your behalf, pay you for completed interviews, and protect the Service
            from abuse.
          </li>
          <li>We don't sell your personal information.</li>
          <li>We don't train general-purpose AI models on your data.</li>
          <li>
            We share data with a small set of vendors that help us operate the Service
            (database, compute, payments, message delivery). They are listed in Section 4.
          </li>
          <li>You can ask us to show you, export, correct, or delete your data at any time.</li>
          <li>
            We store data in the United States (Supabase, Modal, Azure, Stripe). If you are
            accessing the Service from outside the US, you are consenting to that transfer.
          </li>
        </UL>
        <P>
          If you live in the European Economic Area or the United Kingdom, see Section 8. If
          you live in California, see Section 9.
        </P>
      </Section>

      <Section number={2} title="Information we collect">
        <Sub title="2.1 Information you give us directly">
          <P>When you create an account or use the Service, we collect:</P>
          <UL>
            <li>
              <Strong>Account data:</Strong> full name, email address, phone number, language
              preference, time zone, and (if you sign in with Google) your Google profile
              identifier.
            </li>
            <li>
              <Strong>Contact channels:</Strong> WhatsApp phone number, iMessage handle (which
              may be an Apple ID email or phone number), and which of these you used most
              recently.
            </li>
            <li>
              <Strong>Professional profile:</Strong> resume file and parsed text, LinkedIn
              URL, industry, current or most recent job title, years of experience, areas of
              expertise, tools you use, and the city and country you're based in.
            </li>
            <li>
              <Strong>Preferences:</Strong> which days and times you're available, whether
              you're willing to do video/audio interviews, opportunity types you're interested
              in, notification settings, and dark-mode preference.
            </li>
            <li>
              <Strong>Job-application details:</Strong> work-authorization status, sponsorship
              needs, salary expectations, and answers to common employer screening questions.
              If you choose, you may also provide credentials for sites that require an
              account (e.g., Workday). Credentials are stored encrypted; see Section 11.
            </li>
            <li>
              <Strong>Interview answers:</Strong> for paid research interviews, your text
              answers and, where the campaign is conducted in audio or video format, audio and
              video recordings of your responses.
            </li>
            <li>
              <Strong>Tax and payout information:</Strong> name, address, and tax-identification
              information collected on Pareto's behalf by Stripe Connect (we never see your
              full bank-account or card number).
            </li>
          </UL>
        </Sub>

        <Sub title="2.2 Information we generate from your data">
          <UL>
            <li>
              <Strong>AI representation:</Strong> a numeric "embedding" vector that represents
              your profile in a way our matching engine can compare to job descriptions. The
              embedding is derived from your profile but cannot be reversed back into raw text.
            </li>
            <li>
              <Strong>Resume optimization mode and AI-generated documents:</Strong> cover
              letters and tailored resumes our AI produces when applying for jobs on your
              behalf.
            </li>
          </UL>
        </Sub>

        <Sub title="2.3 Information we collect automatically">
          <UL>
            <li>
              <Strong>Conversation history:</Strong> every message you send to and receive
              from our WhatsApp or iMessage bots, tagged with the channel (
              <Code>whatsapp</Code> or <Code>imessage</Code>), is stored so we can preserve
              continuity across devices and improve the bot.
            </li>
            <li>
              <Strong>Behavioral signals:</Strong> when you reply to messages, how long you
              take to respond, how often you accept or decline paid-interview offers, your
              response patterns over time, your inferred time zone, your "responsiveness"
              score, and your interview opt-out streak.
            </li>
            <li>
              <Strong>Application activity:</Strong> which jobs we applied to on your behalf,
              the status of each application, screenshots captured during the auto-apply
              browser session, and any replies routed through your proxy email.
            </li>
            <li>
              <Strong>Device and connection data:</Strong> your IP address, browser
              user-agent, and basic device information. We use this only for rate limiting,
              fraud detection, and basic operational logs.
            </li>
          </UL>
        </Sub>

        <Sub title="2.4 Information we do not collect">
          <UL>
            <li>
              We do not collect government IDs ourselves (Stripe collects them on its own
              systems for tax compliance; we do not see them).
            </li>
            <li>
              We do not collect bank account or full credit card numbers (Stripe handles
              these).
            </li>
            <li>
              We do not use tracking cookies for advertising. The only cookies and
              local-storage values we set are operational — to keep you signed in, remember
              your language and dark-mode preferences, and persist whether you've dismissed
              certain banners.
            </li>
          </UL>
        </Sub>
      </Section>

      <Section number={3} title="How we use your information">
        <P>We use your information to:</P>
        <UL>
          <li>
            <Strong>Match you to jobs.</Strong> We compare your AI embedding to job-description
            embeddings sourced from public job boards and applicant tracking systems, and
            surface the strongest matches.
          </li>
          <li>
            <Strong>Apply for jobs on your behalf.</Strong> With your authorization, we use
            your profile data to fill in application forms in a temporary browser session on a
            serverless compute sandbox.
          </li>
          <li>
            <Strong>Generate tailored documents.</Strong> We use AI models to draft resumes
            and cover letters that emphasize the parts of your background most relevant to
            each job.
          </li>
          <li>
            <Strong>Match you to paid research interviews.</Strong> We share a subset of your
            profile (name and selected professional fields) with Research Buyers' campaign
            systems so they can match you to research projects you're a fit for.
          </li>
          <li>
            <Strong>Process payouts.</Strong> Stripe Connect uses your name and tax info to
            send you the rewards you earned from completed interviews.
          </li>
          <li>
            <Strong>Communicate with you.</Strong> We send operational and match-alert
            messages over WhatsApp, iMessage, or email.
          </li>
          <li>
            <Strong>Improve the Service.</Strong> We may use anonymized, aggregated signals
            (e.g., "users in industry X tend to respond more to message style Y") to improve
            matching, prompts, and the bot itself. We do not use your individual data to train
            general-purpose AI models.
          </li>
          <li>
            <Strong>Prevent fraud and abuse.</Strong> We use behavioral and device signals to
            detect duplicate accounts, abusive activity, and attempts to circumvent our
            limits.
          </li>
          <li>
            <Strong>Comply with law.</Strong> We may use and disclose data as required to
            comply with applicable laws, respond to legal process, or protect our rights or
            the rights of others.
          </li>
        </UL>
        <P>
          We do not use your information for advertising, do not sell it, and do not share it
          with data brokers.
        </P>
      </Section>

      <Section number={4} title="Who we share information with">
        <P>We share data only with the following categories of recipients:</P>

        <Sub title="Infrastructure vendors (under contract)">
          <UL>
            <li>
              <Strong>Supabase</Strong> — our database and authentication provider, located
              in the United States. Stores essentially all of the data described in Section 2.
            </li>
            <li>
              <Strong>Modal</Strong> — our serverless compute provider, located in the United
              States. Runs job-scraping, AI matching, and the temporary browser sandboxes that
              submit job applications on your behalf.
            </li>
            <li>
              <Strong>Azure Blob Storage (Microsoft)</Strong> — stores resume files,
              application screenshots, and audio/video interview recordings.
            </li>
            <li>
              <Strong>Stripe (Stripe Inc. and Stripe Connect)</Strong> — payment processing
              for paid-interview rewards. Stripe collects identity and tax information directly
              under its own privacy policy.
            </li>
          </UL>
        </Sub>

        <Sub title="Communication providers">
          <UL>
            <li>
              <Strong>Meta WhatsApp Business Cloud API (Meta Platforms, Inc.)</Strong> —
              delivers WhatsApp messages between you and our bot.
            </li>
            <li>
              <Strong>Blooio</Strong> — delivers iMessage messages between you and our bot.
            </li>
          </UL>
        </Sub>

        <Sub title="AI model providers (no personally identifying data shared)">
          <UL>
            <li>
              <Strong>Nebius Token Factory</Strong> (DeepSeek and Qwen models) — receives
              job-description text and profile fragments to perform classification,
              form-filling, and content generation.
            </li>
            <li>
              <Strong>Azure AI Foundry</Strong> (Llama models) — receives job-description text
              for filtering.
            </li>
          </UL>
          <P>
            We pass the minimum input required to perform the task. We do <Strong>not</Strong>{' '}
            pass your name, email, phone number, or other directly identifying information to
            AI model providers.
          </P>
        </Sub>

        <Sub title="Research Buyers (only with your consent and matching)">
          <P>
            When you are matched to a paid research interview campaign, the Research Buyer
            receives:
          </P>
          <UL>
            <li>Your first name (or full name, depending on the campaign).</li>
            <li>
              The professional profile attributes that made you a match (e.g., industry, role,
              years of experience, tools).
            </li>
            <li>
              Your interview answers and, where applicable, audio or video recordings of your
              responses.
            </li>
          </UL>
          <P>
            Research Buyers do <Strong>not</Strong> receive your contact information directly.
            All scheduling and communication is routed through Pareto.
          </P>
        </Sub>

        <Sub title="Legal and safety">
          <P>
            We may disclose information when required by law, valid legal process, or to
            protect Pareto, our users, or the public from imminent harm. We will challenge
            requests we believe are improper.
          </P>
        </Sub>

        <Sub title="Corporate transactions">
          <P>
            If Pareto is involved in a merger, acquisition, financing, or sale of assets, your
            information may be transferred as part of that transaction, subject to the
            receiving party honoring this Privacy Policy.
          </P>
        </Sub>
      </Section>

      <Section number={5} title="How long we keep your information">
        <UL>
          <li>
            <Strong>Active accounts.</Strong> We keep your data for as long as your account
            is open and you continue to use the Service.
          </li>
          <li>
            <Strong>Closed accounts.</Strong> Within 30 days of you closing your account or
            asking us to delete your data, we delete or anonymize your personal information,
            with these exceptions:
            <UL>
              <li>
                We retain transactional records related to paid interviews (including 1099 tax
                information for US users) for <Strong>seven years</Strong>, as required by tax
                and accounting laws.
              </li>
              <li>
                We retain abuse-related records (e.g., flags indicating a fraudulent account)
                for up to <Strong>two years</Strong> to prevent the same actor from
                re-registering.
              </li>
              <li>
                We retain backups for up to <Strong>35 days</Strong>; deletions propagate into
                those backups as the backups age out.
              </li>
            </UL>
          </li>
          <li>
            <Strong>Conversation history.</Strong> Kept while your account is active. On
            account deletion, conversation history is deleted along with the rest of your
            profile, subject to the backup window above.
          </li>
          <li>
            <Strong>Audio and video recordings.</Strong> Kept for 90 days after the interview,
            then deleted, unless you've explicitly given a longer license to the Research
            Buyer at the time of the interview.
          </li>
        </UL>
      </Section>

      <Section number={6} title="Your rights and choices">
        <P>Regardless of where you live, you can always:</P>
        <UL>
          <li>
            <Strong>Access your data.</Strong> Request a copy of the data we hold about you.
          </li>
          <li>
            <Strong>Correct your data.</Strong> Edit your profile in the web dashboard or by
            messaging the bot. Or email us if something is off.
          </li>
          <li>
            <Strong>Delete your data.</Strong> Close your account in the web dashboard or by
            emailing <Code>legal@trypareto.ai</Code>.
          </li>
          <li>
            <Strong>Export your data.</Strong> Request a machine-readable export of your
            profile, conversation history, and application history.
          </li>
          <li>
            <Strong>Object or restrict.</Strong> Ask us to stop a specific use of your data
            (e.g., paid-interview matching) without closing your account.
          </li>
          <li>
            <Strong>Turn off notifications.</Strong> Reply "stop", "pause", or "settings" to
            the bot, or update preferences in the web dashboard.
          </li>
          <li>
            <Strong>Revoke auto-apply.</Strong> Disable auto-apply at any time. Already-
            submitted applications cannot be unsubmitted by us, but no further applications
            will be sent.
          </li>
        </UL>
        <P>
          To exercise any of these rights, email <Code>legal@trypareto.ai</Code>. We will
          respond within 30 days. We may need to verify your identity before fulfilling
          certain requests.
        </P>
      </Section>

      <Section number={7} title="How we protect your information">
        <UL>
          <li>
            <Strong>Encryption in transit.</Strong> All data exchanged between your device and
            our systems is protected with TLS 1.2 or higher.
          </li>
          <li>
            <Strong>Encryption at rest.</Strong> Supabase, Modal, Azure, and Stripe encrypt
            data at rest using industry-standard ciphers (AES-256 or equivalent).
          </li>
          <li>
            <Strong>Row-level security (RLS).</Strong> Our database enforces row-level
            security so that one user cannot access another user's records. Stored credentials
            for third-party sites (e.g., Workday) are additionally encrypted within the
            database.
          </li>
          <li>
            <Strong>Access controls.</Strong> Only a small number of authorized Pareto
            personnel have access to production systems, and that access is logged.
          </li>
          <li>
            <Strong>Webhook signature verification.</Strong> Inbound webhooks (Stripe, Blooio
            iMessage, our proxy email service) are signature-verified before any payload is
            processed.
          </li>
          <li>
            <Strong>Rate limiting and abuse detection.</Strong> Behavioral signals and
            IP-based rate limiting protect against credential-stuffing, automated abuse, and
            denial-of-service.
          </li>
        </UL>
        <P>
          No system is perfectly secure. If we become aware of a security incident that
          materially affects your information, we will notify you in accordance with
          applicable law.
        </P>
      </Section>

      <Section number={8} title="Your rights if you live in the EEA or the UK (GDPR)">
        <P>
          If you are in the European Economic Area, the United Kingdom, or Switzerland, the
          General Data Protection Regulation (GDPR) gives you these rights, in addition to
          those in Section 6:
        </P>
        <UL>
          <li>
            <Strong>Right to be informed</Strong> (this Policy).
          </li>
          <li>
            <Strong>Right of access, rectification, and erasure.</Strong>
          </li>
          <li>
            <Strong>Right to restriction of processing.</Strong>
          </li>
          <li>
            <Strong>Right to data portability.</Strong>
          </li>
          <li>
            <Strong>Right to object</Strong> to processing based on our legitimate interests.
          </li>
          <li>
            <Strong>Right to withdraw consent</Strong> for processing that is based on
            consent (e.g., audio/video recording for an optional interview).
          </li>
          <li>
            <Strong>Right to lodge a complaint</Strong> with your local supervisory authority.
          </li>
        </UL>
        <P>
          <Strong>Our legal bases for processing your data are:</Strong>
        </P>
        <UL>
          <li>
            <Strong>Performance of a contract</Strong> — to provide the Service you signed up
            for (matching, applying, paying you).
          </li>
          <li>
            <Strong>Legitimate interests</Strong> — to prevent fraud and abuse, improve the
            Service, and run the business. Where we rely on legitimate interests, we have
            considered the impact on you and concluded that the processing is proportionate.
          </li>
          <li>
            <Strong>Consent</Strong> — for things you opt into specifically (audio/video
            recordings, notifications beyond essential operational ones).
          </li>
          <li>
            <Strong>Legal obligation</Strong> — for tax records and responses to lawful
            requests.
          </li>
        </UL>
        <P>
          <Strong>International transfers.</Strong> Pareto's processors (Supabase, Modal,
          Azure, Stripe, etc.) are based primarily in the United States. We rely on the
          European Commission's Standard Contractual Clauses (or equivalent, e.g., the UK
          International Data Transfer Addendum) as the legal mechanism for these transfers.
        </P>
        <P>
          To exercise any GDPR right, email <Code>legal@trypareto.ai</Code>.
        </P>
      </Section>

      <Section number={9} title="Your rights if you live in California (CCPA / CPRA)">
        <P>
          If you are a California resident, the California Consumer Privacy Act and California
          Privacy Rights Act give you the rights below in addition to Section 6.
        </P>
        <UL>
          <li>
            <Strong>Right to know</Strong> what personal information we collect, the purposes,
            and the categories of recipients with whom we share it (see Sections 2, 3, and 4).
          </li>
          <li>
            <Strong>Right to access</Strong> the specific pieces of personal information we
            hold about you.
          </li>
          <li>
            <Strong>Right to delete</Strong> your personal information, subject to limited
            legal exceptions.
          </li>
          <li>
            <Strong>Right to correct</Strong> inaccurate personal information.
          </li>
          <li>
            <Strong>Right to non-discrimination</Strong> — we won't deny service, charge a
            different price, or provide a different level of service because you exercised a
            privacy right.
          </li>
          <li>
            <Strong>Right to limit use of sensitive personal information.</Strong> We treat
            your government-issued tax identifiers (handled by Stripe), precise geolocation if
            you provide it, and ATS credentials as sensitive. We use these only to provide the
            Service and do not infer characteristics about you from them.
          </li>
        </UL>
        <P>
          <Strong>Sale and sharing.</Strong> We do not sell your personal information and do
          not "share" it for cross-context behavioral advertising as those terms are defined
          under the CPRA.
        </P>
        <P>
          To exercise any of these rights, email <Code>legal@trypareto.ai</Code>. You may also
          designate an authorized agent to make a request on your behalf.
        </P>
      </Section>

      <Section number={10} title="Children's privacy">
        <P>
          The Service is not intended for, and we do not knowingly collect personal
          information from, anyone under 18 years old. If you believe a minor has registered,
          contact <Code>legal@trypareto.ai</Code> and we will delete the account and
          associated data.
        </P>
      </Section>

      <Section number={11} title="Notes on stored credentials">
        <P>
          If you choose to provide credentials for third-party career sites (such as Workday)
          so that auto-apply can complete applications that require an account:
        </P>
        <UL>
          <li>
            Credentials are stored in a JSON field on your account row,{' '}
            <Strong>additionally encrypted</Strong> beyond the standard database encryption at
            rest.
          </li>
          <li>
            They are decrypted only inside the temporary browser sandbox that runs your
            auto-apply, never displayed in the dashboard, and never logged.
          </li>
          <li>
            You can remove stored credentials at any time from the Settings page or by
            emailing us.
          </li>
        </UL>
      </Section>

      <Section number={12} title="Cookies and local storage">
        <P>We use only the minimum browser storage needed to operate the Service:</P>
        <UL>
          <li>An authentication cookie that keeps you signed in.</li>
          <li>
            Local storage values for your language preference, dark-mode preference, and
            whether you've dismissed certain banners.
          </li>
        </UL>
        <P>
          We do not use analytics or advertising cookies. We do not allow third-party tracking
          pixels.
        </P>
      </Section>

      <Section number={13} title="Changes to this Policy">
        <P>
          We may update this Privacy Policy from time to time. If we make a material change —
          for example, sharing data with a new category of recipient — we'll let you know in
          advance by email and/or via a notice in the dashboard and a message from the bot.
          The current version is always at <Code>trypareto.ai/privacy</Code>.
        </P>
      </Section>

      <Section number={14} title="Contact us">
        <P>
          <Strong>Pareto Labs Inc</Strong>
          <br />
          Email: <Code>legal@trypareto.ai</Code>
        </P>
        <P>
          For privacy-related requests, please include the email address and (if applicable)
          phone number associated with your account so we can locate your data.
        </P>
      </Section>
    </LegalLayout>
  );
}
